import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from "uuid";
import { Select, Collapse, Divider } from "antd";
import Layout from "@components/wrappers/layouts/main-layout/Layout";
import { updateActiveModule } from "@features/activeModuleSlice";
import { updateTitle } from "@features/titleSlice";
import { fetchCountriesData } from "@ts/utils";
import {
    addPortalsState,
    getIntegrationsAppId,
    getPortalUrl,
} from "@ts/retrieve-data";
import { ReactComponent as AmazonLogo } from "@images/amazon_icon.svg";
import { regionOptions } from "@ts/constants";
import { FaExchangeAlt } from "react-icons/fa";
import logo from "@images/1ds_logo.jpg";
import { EAccountType, ICountryData } from "@/ts/common-types";

const Integrations = () => {
    const dispatch = useDispatch();
    const { Option } = Select;
    const channel = 1;
    const [countryOptions, setCountryOptions] = useState([
        {
            value: "",
            label: "--- Select Country ---",
        },
    ]);
    const [appId, setAppId] = useState("");
    const [states, setStates] = useState<string[]>([]);
    const [regionSelectedOption, setRegionSelectedOption] = useState(
        regionOptions[0].value
    );
    const [countrySelectedOption, setCountrySelectedOption] = useState(
        countryOptions[0].value
    );
    const [avlLinks, setAvlLinks] = useState();
    const [redirectUri] = useState(
        "https://oauth.1digitalstack.ai/integration/portal"
    );
    const onChangeCountryHandler = (option: string) =>
        setCountrySelectedOption(option);
    const onChangeRegionHandler = (option: string) =>
        setRegionSelectedOption(option);

    const populateLink = async () => {
        try {
            const authLinks = await onConnect();
            if (!authLinks) {
                throw new Error("Unable to get links!!");
            }
            // TODO: Advertising to be added
            setAvlLinks(authLinks);
        } catch (err: any) {
            console.error(err.message);
        }
    };

    const onConnect = async () => {
        try {
            const appIdResponse = await getIntegrationsAppId();
            if (!appIdResponse) {
                throw new Error(appIdResponse.message);
            }
            setAppId(appIdResponse.amazonAppId);
            const stateUUID = uuidv4();
            //TODO: Disabled advertising
            const stateArr = [
                `${EAccountType.SELLER}-${stateUUID}`,
                `${EAccountType.VENDOR}-${stateUUID}`,
            ];
            setStates(stateArr);
            const stateResponse = await addPortalsState(stateArr);

            if (!stateResponse) {
                throw new Error(stateResponse.message);
            }

            const urlResponse = await getPortalUrl(
                channel,
                countrySelectedOption,
                regionSelectedOption
            );

            if (!urlResponse) {
                throw new Error(urlResponse.message);
            }
            return urlResponse;
        } catch (err: any) {
            console.error(err.message);
        }
    };

    const onConnectButtonClickHandler = (type: string) => {
        const baseURL = (avlLinks as Record<string, string>[]).find(
            (link: Record<string, string>) => link.account_type === type
        );

        const stId = states.find((st: string) => st.split("-")[0] === type);

        const params = new URLSearchParams({
            application_id: appId,
            version: process.env.VITE_NODE_ENV === "local" ? "beta" : "",
            state: stId,
            redirect_uri: redirectUri,
        });

        const linkToRedirect = `${
            baseURL.portal_url
        }/apps/authorize/consent?${params.toString()}`;

        const redirectWindow = window.open(
            linkToRedirect,
            "_blank",
            "height=600,width=600"
        );

        if (redirectWindow) {
            redirectWindow.focus();
        }
    };

    useEffect(() => {
        if (countrySelectedOption && regionSelectedOption) {
            populateLink();
        }
    }, [countrySelectedOption, regionSelectedOption]);

    useEffect(() => {
        dispatch(updateTitle("Integrations"));
        dispatch(updateActiveModule("integrations"));
        fetchCountriesData()
            .then((countriesData) => {
                if (countriesData && countriesData.length) {
                    const formattedOptions = countriesData.map(
                        (cd: ICountryData) => ({
                            value: cd.alpha2_code,
                            label: `${cd.name} (${cd.alpha2_code})`,
                        })
                    );
                    setCountryOptions((prev) => [...prev, ...formattedOptions]);
                }
            })
            .catch((err: any) => {
                console.error(err);
                Sentry.captureException(err);
            });
        //eslint-disable-next-line
    }, []);

    return (
        <Layout>
            <div className="integrations-div">
                <div className="integrations-container card shadow">
                    <div className="d-flex align-items-center p-3">
                        <AmazonLogo width="3.5rem" height="3.5rem" />
                        <h3 className="mx-3 fw-bolder">
                            Integrate with Amazon
                        </h3>
                    </div>
                    <Collapse
                        className="integration-collapse-container"
                        accordion
                        size="large"
                        items={[
                            {
                                showArrow: false,
                                key: "1",
                                style: {
                                    borderRadius: "0",
                                },
                                label: <b>Select a Region</b>,
                                children: (
                                    <div className="integrations-select-container">
                                        <div className="integrations-select-label">
                                            Please select the Region for your
                                            account
                                        </div>
                                        <Select
                                            showSearch
                                            className="country-selector"
                                            value={regionSelectedOption}
                                            onChange={onChangeRegionHandler}
                                            size="large"
                                        >
                                            {regionOptions.map(
                                                (data: any, index: number) => (
                                                    <Option
                                                        key={index + 1}
                                                        value={data.value}
                                                    >
                                                        {data.label}
                                                    </Option>
                                                )
                                            )}
                                        </Select>
                                    </div>
                                ),
                            },
                            {
                                showArrow: false,
                                key: "2",
                                style: {
                                    borderRadius: "0",
                                },
                                label: <b>Select a Country</b>,
                                children: (
                                    <div className="integrations-select-container">
                                        <div className="integrations-select-label">
                                            Please select the Country for your
                                            account
                                        </div>
                                        <Select
                                            showSearch
                                            className="country-selector me-3"
                                            value={countrySelectedOption}
                                            onChange={onChangeCountryHandler}
                                            size="large"
                                        >
                                            {countryOptions.map(
                                                (data: any, index: number) => (
                                                    <Option
                                                        key={index + 1}
                                                        value={data.value}
                                                    >
                                                        {data.label}
                                                    </Option>
                                                )
                                            )}
                                        </Select>
                                    </div>
                                ),
                            },
                            {
                                showArrow: false,
                                key: "3",
                                style: {
                                    borderRadius: "0",
                                },
                                label: <b>Connect Account</b>,
                                children: (
                                    <div className="integrations-select-container">
                                        <div
                                            style={{
                                                textAlign: "center",
                                                padding: "25px 0 20px  0",
                                            }}
                                        >
                                            <img
                                                className="integrations-connect-logo"
                                                src={logo}
                                                alt="1ds-logo"
                                            />
                                            <FaExchangeAlt
                                                style={{
                                                    fontSize: "50px",
                                                    color: "gray",
                                                    margin: "auto 20px",
                                                }}
                                            />
                                            <AmazonLogo className="integrations-connect-logo" />
                                        </div>
                                        <div className="d-flex justify-content-center align-items-center my-3">
                                            {avlLinks ? (
                                                <>
                                                    <button
                                                        className="btn btn-primary connect-amazon-btn"
                                                        onClick={() => {
                                                            onConnectButtonClickHandler(
                                                                "seller"
                                                            );
                                                        }}
                                                    >
                                                        Connect Seller
                                                    </button>
                                                    <Divider
                                                        type="vertical"
                                                        className="connect-amazon-btn-divider"
                                                    />
                                                    <button
                                                        className="btn btn-primary connect-amazon-btn"
                                                        onClick={() => {
                                                            onConnectButtonClickHandler(
                                                                EAccountType.VENDOR
                                                            );
                                                        }}
                                                    >
                                                        Connect Vendor
                                                    </button>
                                                    {/* <Divider type="vertical" className="connect-amazon-btn-divider" />
                                                <button className='btn btn-primary connect-amazon-btn'>Connect Advertising</button> */}
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                ),
                            },
                        ]}
                    />
                </div>
            </div>
        </Layout>
    );
};

export default Integrations;
